import { UIPlugin, Uppy } from '@uppy/core';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
pdfjsLib.GlobalWorkerOptions.workerSrc = '/lib/pdf.worker.js';

interface RestrictPdfPagesOptions {
  id?: string;
  maxPages?: number;
}

// only supported for PDF files

class RestrictPdfPages extends UIPlugin {
  opts: RestrictPdfPagesOptions;
  restrictPages: (fileIDs: string[]) => Promise<void>;

  constructor(uppy: Uppy, opts: RestrictPdfPagesOptions = {}) {
    const defaultOptions = { maxPages: 15 };
    super(uppy, opts);
    this.opts = { ...defaultOptions, ...opts };
    this.id = this.opts.id || 'RestrictPdfPages';
    this.type = 'modifier';
    this.restrictPages = this.restrictPagesImpl.bind(this);
  }

  async restrictPagesImpl(fileIDs: string[]): Promise<void> {
    const promises = fileIDs.map(async (fileID) => {
      const file = this.uppy.getFile(fileID);
      if (!file || file.type !== 'application/pdf') return;

      this.uppy.emit('preprocess-progress', file, {
        mode: 'indeterminate',
        message: 'Checking PDF page count',
      });

      try {
        const arrayBuffer = await file.data.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        const pageCount = pdf.numPages;

        if (pageCount > (this.opts.maxPages ?? 15)) {
          this.uppy.info(
            `"${file.name}" has ${pageCount} pages. Limit is ${this.opts.maxPages ?? 15}.`,
            'error',
            5000,
          );

          this.uppy.emit('restriction-failed', file, {
            message: `PDF must not exceed ${this.opts.maxPages} pages (found ${pageCount})`,
          });

          this.uppy.removeFile(file.id);
        }
      } catch (err) {
        console.error('Error checking PDF pages', err);
        this.uppy.info(`Failed to read PDF: ${file.name}`, 'error', 4000);
        this.uppy.removeFile(file.id);
      }
    });

    return Promise.all(promises).then(() => {
      fileIDs.forEach((fileID) => {
        const file = this.uppy.getFile(fileID);
        if (file) {
          this.uppy.emit('preprocess-complete', file);
        }
      });
    });
  }

  install(): void {
    this.uppy.addPreProcessor(this.restrictPages);
  }

  uninstall(): void {
    this.uppy.removePreProcessor(this.restrictPages);
  }
}

export default RestrictPdfPages;
